import { DeliveryDecisionReason, DeliveryStatus, NotificationChannelKey } from './graphql';

// #737: Uebersetzung der rohen Enum-Namen aus dem Audit-DTO.
//
// Anders als beim Katalog (label/categoryLabel) liefert das Backend fuer status, decisionReason und
// channel KEINE uebersetzten Bezeichnungen. Die Zuordnung Enum-Wert -> i18n-Schluessel liegt deshalb
// hier zentral, damit Zeile, Filter, Dialog und der Vollstaendigkeitstest dieselbe Quelle benutzen.
//
// typeKey bleibt bewusst technisch: im Audit-DTO gibt es kein Label, und fuer Support ist der
// Schluessel die brauchbarere Information.

export const NOTIFICATION_DIAGNOSTICS_I18N_PREFIX = 'core.notificationDiagnostics';

/** Unbekannte Werte (neuer Server-Enum-Wert vor dem naechsten Web-Release) werden roh angezeigt. */
function keyFor(group: string, value: string | null | undefined, known: readonly string[]): string {
  if (!value) {
    return '';
  }
  return known.includes(value) ? `${NOTIFICATION_DIAGNOSTICS_I18N_PREFIX}.${group}.${value}` : value;
}

export const DELIVERY_STATUS_VALUES: readonly DeliveryStatus[] = Object.values(DeliveryStatus);
export const DELIVERY_REASON_VALUES: readonly DeliveryDecisionReason[] = Object.values(DeliveryDecisionReason);
export const DELIVERY_CHANNEL_VALUES: readonly NotificationChannelKey[] = Object.values(NotificationChannelKey);

export function deliveryStatusLabelKey(status: string | null | undefined): string {
  return keyFor('status', status, DELIVERY_STATUS_VALUES);
}

export function deliveryReasonLabelKey(reason: string | null | undefined): string {
  return keyFor('reason', reason, DELIVERY_REASON_VALUES);
}

export function deliveryChannelLabelKey(channel: string | null | undefined): string {
  return keyFor('channel', channel, DELIVERY_CHANNEL_VALUES);
}

export type TagSeverity = 'success' | 'info' | 'warning' | 'danger';

/**
 * p-tag-Severity je Status. SENDING gilt als laufend (info), nicht als Problem — eine haengende
 * Zustellung erkennt Support an der Zeit, nicht an der Farbe; die Stale-Recovery holt sie ein.
 */
export function deliveryStatusSeverity(status: string | null | undefined): TagSeverity {
  switch (status) {
    case DeliveryStatus.SENT:
      return 'success';
    case DeliveryStatus.FAILED:
      return 'danger';
    case DeliveryStatus.SKIPPED:
      return 'warning';
    default:
      return 'info';
  }
}

/**
 * Ein Eintrag einer Filter-Auswahl. Steht hier, weil dieselbe Form von beiden Listen und vom
 * Container benutzt wird: `label` ist entweder ein i18n-Schluessel (Status, Kanal) oder ein bereits
 * uebersetzter Text vom Server (Katalog-Label) — welches von beidem, entscheidet die Herkunft,
 * nicht diese Form.
 */
export interface FilterOption {
  label: string;
  value: string;
}

/** Wie laut eine Kanalzelle in der Nachrichtenliste sein soll. */
export type ChannelCellWeight = 'finding' | 'hint' | 'normal';

/**
 * Gruende, die einen Supportfall ERKLAEREN — im Gegensatz zu denen, die eine Entscheidung des
 * Nutzers oder des Typs wiedergeben. Nur diese werden hervorgehoben.
 */
const HINT_REASONS: readonly string[] = [
  DeliveryDecisionReason.NO_DEVICE,
  DeliveryDecisionReason.NO_EMAIL_ADDRESS,
  DeliveryDecisionReason.USER_DEACTIVATED,
  DeliveryDecisionReason.NO_FULL_LICENCE,
  DeliveryDecisionReason.CHANNEL_DISABLED
];

/**
 * Gewichtung einer Kanalzelle aus dem PAAR Status und Grund.
 *
 * Nicht zu verwechseln mit deliveryStatusSeverity: die kennt nur den Status und macht aus jedem
 * SKIPPED eine Warnung. Das ist in der flachen Liste richtig, wo der Grund ohnehin im Klartext
 * danebensteht — hier waere es falsch. „Der Nutzer hat den Kanal abgewaehlt" ist eine Erklaerung
 * und kein Defekt und gehoert leise; „kein Geraet registriert" erklaert den Supportfall.
 *
 * Unbekannte Werte (neuer Server-Enum-Wert vor dem naechsten Web-Release) gelten als Normalfall —
 * eine unbekannte Lage darf die Liste nicht rot faerben.
 */
export function channelCellWeight(
  status: string | null | undefined,
  reason: string | null | undefined
): ChannelCellWeight {
  if (status === DeliveryStatus.FAILED) {
    return 'finding';
  }
  if (status === DeliveryStatus.SKIPPED && reason && HINT_REASONS.includes(reason)) {
    return 'hint';
  }
  return 'normal';
}

/**
 * Was in der Zelle steht. Bei SKIPPED der GRUND, sonst der Status: „Uebersprungen" allein
 * beantwortet keine Frage, „kein Geraet" schon.
 */
export function channelCellLabelKey(
  status: string | null | undefined,
  reason: string | null | undefined
): string {
  return status === DeliveryStatus.SKIPPED
    ? deliveryReasonLabelKey(reason)
    : deliveryStatusLabelKey(status);
}

/**
 * Die p-tag-Severity einer Kanalzelle. Jede Zelle traegt ein Tag — dieselbe Darstellung wie die
 * Zustellzeilen im Detail; die Gewichtung steckt in der FARBE statt im Wechsel zwischen Tag und
 * grauem Text.
 *
 * Der Unterschied zu deliveryStatusSeverity liegt allein bei SKIPPED: die macht daraus immer eine
 * Warnung. Hier entscheidet das Paar aus Status und Grund — „der Nutzer hat den Kanal abgewaehlt"
 * ist eine Erklaerung und kein Defekt und bleibt neutral, „kein Geraet registriert" erklaert den
 * Supportfall und wird zur Warnung.
 */
export function channelCellSeverity(
  status: string | null | undefined,
  reason: string | null | undefined
): TagSeverity {
  switch (channelCellWeight(status, reason)) {
    case 'finding':
      return 'danger';
    case 'hint':
      return 'warning';
    default:
      return status === DeliveryStatus.SKIPPED ? 'info' : deliveryStatusSeverity(status);
  }
}
